import { useRef, useState } from 'react';
import { AlertTriangle, Store } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext.js';

/**
 * Import-winkel-bevestiging — voorkomt dat data in de verkeerde winkel geïmporteerd wordt.
 *
 * Toont altijd in welke winkel een import terechtkomt. Heeft de gebruiker toegang tot
 * meerdere winkels (switcher), dan moet hij dat eerst aanvinken; tot dan weigeren de
 * upload-handlers (guard.bevestigd === false).
 *
 *   const winkel = useImportWinkel();
 *   <ImportWinkelBanner guard={winkel} />
 *   const onDrop = (files) => { if (!winkel.bevestigd) return; … }
 */
export interface ImportWinkelGuard {
  winkelNaam: string;
  meerdere: boolean;
  bevestigd: boolean;
  setBevestigd: (v: boolean) => void;
  /** Voor gebruik in useCallback-handlers (geen stale closure). */
  isBevestigd: () => boolean;
}

export function useImportWinkel(): ImportWinkelGuard {
  const { activeEntity, accessibleEntities } = useAuth();
  const meerdere = accessibleEntities.length > 1;
  const [bevestigd, setBevestigdState] = useState(false);
  const ref = useRef(false);
  const setBevestigd = (v: boolean) => { ref.current = v; setBevestigdState(v); };
  const isBevestigd = () => !meerdere || ref.current;
  return { winkelNaam: activeEntity?.name ?? 'onbekende winkel', meerdere, bevestigd: !meerdere || bevestigd, setBevestigd, isBevestigd };
}

export function ImportWinkelBanner({ guard }: { guard: ImportWinkelGuard }) {
  const { winkelNaam, meerdere, bevestigd, setBevestigd } = guard;
  const waarschuw = meerdere && !bevestigd;
  return (
    <div className={waarschuw
      ? 'flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900'
      : 'flex items-center gap-3 rounded-xl border border-card-border bg-card px-4 py-3 text-gray-700'}>
      {waarschuw ? <AlertTriangle size={20} className="mt-0.5 shrink-0" /> : <Store size={18} className="shrink-0 text-brand" />}
      <div className="flex-1 text-sm">
        <div>Je importeert in <strong>{winkelNaam}</strong>.</div>
        {meerdere && (
          <div className="mt-1 text-xs opacity-80">
            Je hebt toegang tot meerdere winkels. Verkeerde winkel? Wissel eerst via de winkel-switcher rechtsboven.
          </div>
        )}
        {meerdere && (
          <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm font-medium">
            <input type="checkbox" className="accent-brand" checked={bevestigd} onChange={(e) => setBevestigd(e.target.checked)} />
            Ja, dit is voor {winkelNaam}
          </label>
        )}
      </div>
    </div>
  );
}
